import { Mail, Phone, MapPin, ArrowRight, Linkedin } from "lucide-react";
import { BRAND } from "../lib/content";

export default function Contact() {
  const emailSubject = encodeURIComponent(
    "Discovery Call request — Accrue Metrics"
  );
  const emailBody = encodeURIComponent(
    "Hi Accrue Metrics team,\n\nFirm name:\nMy role:\nFirm size:\nService of interest (Bookkeeping / Process Automation / Finance AI Agents):\n\nA little about what we'd like help with:\n\nThanks,"
  );

  return (
    <div data-testid="page-contact">
      {/* HEADER */}
      <section className="bg-[#0A1F44] py-24 text-white lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid items-end gap-10 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <div className="text-xs uppercase tracking-[0.22em] text-[#C9A227]">
                Contact
              </div>
              <h1 className="mt-4 font-serif text-5xl font-medium leading-[1.05] tracking-tight text-white sm:text-6xl lg:text-7xl">
                Let's map your back office
                <br />
                <span className="italic text-[#C9A227]">in 30 minutes.</span>
              </h1>
            </div>
            <div className="lg:col-span-4">
              <p className="text-lg leading-relaxed text-white/75">
                Send us a note or call directly. Every inquiry is read by a
                partner — expect a reply within one business day.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT CARDS */}
      <section className="bg-white py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid gap-6 lg:grid-cols-3">
            {/* EMAIL CARD */}
            <a
              href={`mailto:${BRAND.email}?subject=${emailSubject}&body=${emailBody}`}
              data-testid="contact-card-email"
              className="card-hover group flex flex-col justify-between overflow-hidden rounded-sm border border-[#E5E7EB] bg-white p-10"
            >
              <div>
                <div className="flex h-12 w-12 items-center justify-center rounded-sm bg-[#0A1F44]/5 text-[#0A1F44]">
                  <Mail className="h-5 w-5" />
                </div>
                <div className="mt-7 text-[10px] uppercase tracking-[0.22em] text-[#C9A227]">
                  Email us
                </div>
                <h3 className="mt-2 font-serif text-2xl font-semibold text-[#0A1F44]">
                  {BRAND.email}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-[#475467]">
                  Our preferred channel. We reply to every email within one
                  business day.
                </p>
              </div>
              <div className="mt-10 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#0A1F44] transition-colors group-hover:text-[#C9A227]">
                Compose email
                <ArrowRight className="h-3.5 w-3.5" />
              </div>
            </a>

            {/* PHONE CARD */}
            <a
              href={`tel:${BRAND.phone.replace(/[^0-9+]/g, "")}`}
              data-testid="contact-card-phone"
              className="card-hover group flex flex-col justify-between overflow-hidden rounded-sm border border-[#E5E7EB] bg-white p-10"
            >
              <div>
                <div className="flex h-12 w-12 items-center justify-center rounded-sm bg-[#0A1F44]/5 text-[#0A1F44]">
                  <Phone className="h-5 w-5" />
                </div>
                <div className="mt-7 text-[10px] uppercase tracking-[0.22em] text-[#C9A227]">
                  Call us
                </div>
                <h3 className="mt-2 font-serif text-2xl font-semibold text-[#0A1F44]">
                  {BRAND.phone}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-[#475467]">
                  Mon – Fri, 9 AM to 6 PM PT. Ask for the partner on duty for
                  new engagements.
                </p>
              </div>
              <div className="mt-10 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#0A1F44] transition-colors group-hover:text-[#C9A227]">
                Call now
                <ArrowRight className="h-3.5 w-3.5" />
              </div>
            </a>

            {/* OFFICE CARD */}
            <div
              data-testid="contact-card-office"
              className="flex flex-col justify-between overflow-hidden rounded-sm border border-[#E5E7EB] bg-[#0A1F44] p-10 text-white"
            >
              <div>
                <div className="flex h-12 w-12 items-center justify-center rounded-sm bg-white/10 text-[#C9A227]">
                  <MapPin className="h-5 w-5" />
                </div>
                <div className="mt-7 text-[10px] uppercase tracking-[0.22em] text-[#C9A227]">
                  Visit us
                </div>
                <h3 className="mt-2 font-serif text-2xl font-semibold text-white">
                  Headquarters
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-white/70">
                  {BRAND.address}
                </p>
              </div>
              <div className="mt-10 text-[10px] uppercase tracking-[0.22em] text-white/55">
                By appointment only
              </div>
            </div>
          </div>

          {/* PRIMARY CTA */}
          <div className="mt-16 rounded-sm border border-[#E5E7EB] bg-[#FAFAFB] p-10 lg:p-16">
            <div className="grid items-center gap-10 lg:grid-cols-12">
              <div className="lg:col-span-8">
                <div className="text-xs uppercase tracking-[0.22em] text-[#C9A227]">
                  Ready when you are
                </div>
                <h2 className="mt-3 font-serif text-3xl font-medium leading-tight tracking-tight text-[#0A1F44] sm:text-4xl">
                  Book a 30-minute discovery call.
                </h2>
                <p className="mt-4 max-w-2xl text-base leading-relaxed text-[#475467]">
                  Send us a short note about your firm — we'll come back with
                  three workflows we'd take off your team's plate first, plus a
                  link to pick a time that suits your calendar.
                </p>
              </div>
              <div className="flex flex-wrap gap-4 lg:col-span-4 lg:justify-end">
                <a
                  href={`mailto:${BRAND.email}?subject=${emailSubject}&body=${emailBody}`}
                  data-testid="contact-primary-cta"
                  className="hover-lift inline-flex items-center gap-3 rounded-sm bg-[#0A1F44] px-7 py-4 text-sm font-semibold text-white hover:bg-[#071630]"
                >
                  <Mail className="h-4 w-4" />
                  Email a partner
                  <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT HAPPENS NEXT */}
      <section className="bg-[#FAFAFB] py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.22em] text-[#C9A227]">
              What happens next
            </div>
            <h2 className="mt-4 font-serif text-4xl font-medium leading-tight tracking-tight text-[#0A1F44] sm:text-5xl">
              From inbox to engagement.
            </h2>
          </div>

          <div className="mt-14 grid gap-px overflow-hidden rounded-sm border border-[#E5E7EB] bg-[#E5E7EB] md:grid-cols-3">
            {[
              {
                n: "01",
                t: "A partner reads your note",
                d: "Every message is reviewed by a senior partner — not a chatbot or routing script.",
              },
              {
                n: "02",
                t: "We send a scoping link",
                d: "Within one business day you'll receive three workflow ideas and a Calendly link.",
              },
              {
                n: "03",
                t: "Two-week pilot",
                d: "If we're a mutual fit, we onboard 3–5 of your clients and prove the model in two weeks.",
              },
            ].map((step) => (
              <div
                key={step.n}
                className="bg-white p-10 transition-colors hover:bg-[#FAFAFB]"
              >
                <div className="font-serif text-3xl text-[#C9A227]">{step.n}</div>
                <h3 className="mt-4 font-serif text-2xl font-semibold text-[#0A1F44]">
                  {step.t}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-[#475467]">
                  {step.d}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SOCIAL / FOLLOW */}
      <section className="bg-white py-20 lg:py-24">
        <div className="mx-auto flex max-w-7xl flex-col items-start gap-8 px-6 lg:flex-row lg:items-center lg:justify-between lg:px-8">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-[#C9A227]">
              Stay in touch
            </div>
            <h2 className="mt-3 font-serif text-3xl font-medium leading-tight text-[#0A1F44] sm:text-4xl">
              Follow the firm behind the scenes.
            </h2>
          </div>
          <div className="flex gap-3">
            <a
              href="https://www.linkedin.com/company/accruemetrics/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-linkedin"
              className="hover-lift inline-flex items-center gap-3 rounded-sm border border-[#E5E7EB] bg-white px-5 py-3 text-sm font-semibold text-[#0A1F44] hover:border-[#0A1F44]"
            >
              <Linkedin className="h-4 w-4" />
              LinkedIn
            </a>
            <a
              href={`mailto:${BRAND.email}`}
              data-testid="contact-simple-email"
              className="hover-lift inline-flex items-center gap-3 rounded-sm bg-[#C9A227] px-5 py-3 text-sm font-semibold text-[#0A1F44] hover:bg-[#B38F22]"
            >
              <Mail className="h-4 w-4" />
              {BRAND.email}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
